import React from 'react';
import Tooltip from 'react/components/common/Tooltip/Tooltip';
import { convertStringToFloat } from 'react/utils/NumberUtils';
import { formatNumber } from './tableFormatters';

/**
 * Render interest-portion template
 */
export const renderInterestPortion = (row) => {
  const tooltipContent = `${(row['interest-portion'] || 0).toFixed(3)}% of selected audience consumed ${
    row['phrase'] || row['title'] || ''
  }`;
  return (
    <Tooltip content={tooltipContent}>
      <span>{(row['interest-portion'] || 0).toFixed(3)}%</span>
    </Tooltip>
  );
};

/**
 * Render segment-portion template
 */
export const renderSegmentPortion = (row) => {
  const tooltipContent = `${(row['segment-portion'] || 0).toFixed(3)}% of ${
    row.phrase || row.title || ''
  } consumption is by selected audience`;
  return (
    <Tooltip content={tooltipContent}>
      <span>{(row['segment-portion'] || 0).toFixed(3)}%</span>
    </Tooltip>
  );
};

/**
 * Render composition-index template
 */
export const renderCompositionIndex = (row) => {
  const uniqueness = row['uniqueness-index'] || 0;
  const fixedUniqueness = uniqueness.toFixed(2);
  let tooltipText = '';

  if (+fixedUniqueness === 1) {
    tooltipText = `Consumers of ${
      row.phrase || row.title || ''
    } are as likely to be in the target audience as the average consumer.`;
  } else if (+fixedUniqueness === 2) {
    tooltipText = `Consumers of ${
      row.phrase || row.title || ''
    } are unlikely to be in the target audience as the average consumer.`;
  } else {
    const multiplier = +fixedUniqueness > 1 ? +fixedUniqueness : (1 / uniqueness).toFixed(2);
    const moreOrLess = +fixedUniqueness > 1 ? 'more' : 'less';
    tooltipText = `Consumers of ${
      row.phrase || row.title || ''
    } are ×${multiplier} times ${moreOrLess} likely to be in the target audience than the average consumer.`;
  }

  return (
    <Tooltip content={tooltipText}>
      <span>{uniqueness.toFixed(2)}</span>
    </Tooltip>
  );
};

/**
 * Render cell content based on column configuration
 * @param {Object} row - Row data
 * @param {Object} column - Column configuration
 * @returns {React.ReactNode} Rendered cell content
 */
export const renderCellContent = (row, column) => {
  // Handle template-based columns
  if (column.template) {
    switch (column.template) {
      case 'partials/interest-portion.partial.html':
        return renderInterestPortion(row);
      case 'partials/segment-portion.partial.html':
        return renderSegmentPortion(row);
      case 'partials/composition-index.partial.html':
        return renderCompositionIndex(row);
      default:
        // For unknown templates, try to render the column.id value
        if (column.displayValue) {
          return row[column.displayValue];
        }
        if (column.nonFormatable) {
          return row[column.id];
        }
        // Only format if it's a number
        const value = row[column.id];
        return typeof value === 'number' || (typeof value === 'string' && !isNaN(convertStringToFloat(value)))
          ? formatNumber(value, 2)
          : value;
    }
  }

  // Handle regular columns
  if (column.displayValue) {
    return (
      <>
        {row[column.displayValue]}
        {column.sym || ''}
      </>
    );
  }
  if (column.nonFormatable) {
    return (
      <>
        {row[column.id]}
        {column.sym || ''}
      </>
    );
  }

  // Only format if it's a number
  const value = row[column.id];
  const shouldFormat = typeof value === 'number' || (typeof value === 'string' && !isNaN(convertStringToFloat(value)));
  return (
    <>
      {shouldFormat ? formatNumber(value, 2) : value}
      {column.sym || ''}
    </>
  );
};
